import React from 'react';

// Chart primitive shared by the Usage cards (Server tab) and the player
// activity chart (Activity tab). Renders inside a `.v-srv-charts .o-card`
// scope — the container supplies the dark card styling.

export const BLUE   = '#5b8ff9';
export const PURPLE = '#b26bf3';

/** 1..10 → 10, 11..20 → 20, 21..50 → 50 … so the top gridline lands on a round number. */
export function niceCeil(value: number) {
  if (value <= 0) return 1;

  const power    = Math.pow(10, Math.floor(Math.log10(value)));
  const mantissa = value / power;
  const factor   = mantissa <= 1 ? 1 : mantissa <= 2 ? 2 : mantissa <= 5 ? 5 : 10;

  return factor * power;
}

export function percentLabel(value: number) {
  return `${Math.round(value)}%`;
}

export function countLabel(value: number) {
  return `${Math.round(value)}`;
}

export function bytesLabel(value: number) {
  if (value <= 0) return '0 B';

  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let scaled = value;
  let unit = 0;

  while (scaled >= 1024 && unit < units.length - 1) {
    scaled /= 1024;
    unit += 1;
  }

  const decimals = scaled >= 100 || Number.isInteger(scaled) ? 0 : scaled >= 10 ? 1 : 2;

  return `${scaled.toFixed(decimals)} ${units[unit]}`;
}

// Network counters are cumulative totals from Wings — the chart wants the
// per-sample movement. A restart resets the counter, which would read as a
// huge negative spike; clamp those to zero instead.
export function deltas(values: number[]) {
  return values.map((value, index) =>
    index === 0 ? 0 : Math.max(0, value - values[index - 1])
  );
}

export interface SeriesInterface {
  color: string;
  values: number[];
}

// One filled area per series in a 0-100 viewBox stretched to the card
// (preserveAspectRatio='none'); `vector-effect` keeps the stroke width
// honest under that non-uniform scaling.
const AreaChart = ({ id, series, format }: { id: string, series: SeriesInterface[], format: (value: number) => string }) => {
  const max = niceCeil(Math.max(0, ...series.flatMap(entry => entry.values)));

  const coords = (values: number[]) => {
    // A single sample still draws as a flat line instead of nothing.
    const points = values.length === 1 ? [values[0], values[0]] : values;

    return points.map((value, index) => {
      const x = (index / (points.length - 1)) * 100;
      const y = 100 - Math.min(100, (value / max) * 100);

      return `${x.toFixed(2)},${y.toFixed(2)}`;
    });
  };

  return (
    <div className='o-plot'>
      {[0, 50].map(top => (
        <div key={top} className='o-gridline' style={{ top: `${top}%` }}>
          <span>{format(max * (1 - top / 100))}</span>
        </div>
      ))}
      <div className='o-gridline o-zero' style={{ top: '100%' }}>
        <span>{format(0)}</span>
      </div>

      <svg viewBox='0 0 100 100' preserveAspectRatio='none'>
        <defs>
          {series.map((entry, index) => (
            <linearGradient key={index} id={`v-srv-grad-${id}-${index}`} x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={entry.color} stopOpacity={0.45} />
              <stop offset='100%' stopColor={entry.color} stopOpacity={0.05} />
            </linearGradient>
          ))}
        </defs>

        {series.map((entry, index) => {
          if (!entry.values.length) return null;

          const line = `M${coords(entry.values).join('L')}`;

          return (
            <g key={index}>
              <path d={`${line}L100,100L0,100Z`} fill={`url(#v-srv-grad-${id}-${index})`} />
              <path
                d={line}
                fill='none'
                stroke={entry.color}
                strokeWidth={2}
                strokeLinejoin='round'
                vectorEffect='non-scaling-stroke'
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default AreaChart;
